import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  FaCheck,
  FaClipboardList,
  FaEdit,
  FaMapMarkerAlt,
  FaTimes,
} from "react-icons/fa";
import Swal from "sweetalert2";
import { CURRENCY_OPTIONS } from "../../../Applications/ApplicationCostSection";
import {
  createNewCommissionPlan,
  extractCreatedPlanId,
  updateNewCommissionPlan,
} from "../commissionsApi";
import { formatCurrency, formatPercent } from "./commissionDetailConstants";
import SetupCardSavedBody from "./commissionSetupCardLayout";
import { FeeAmountStack } from "./PaidBalanceLines";
import {
  buildNewCommissionPlanPayload,
  calculateCommissionGstViews,
  calculateExpectedCommission,
  COMMISSION_TYPE_OPTIONS,
  DEFAULT_COMMISSION_GST_PERCENT,
  isBonusFromCommissionableEnabled,
  isFixedCommissionType,
  resolveEffectiveCommissionableTuitionFee,
  resolveRateForLocation,
} from "./commissionPlanUtils";
import { commissionPlanGstPair } from "./commissionPlanSummaryUtils";

const controlClass =
  "h-9 w-full rounded-lg border border-slate-200 bg-white px-2.5 text-[12px] font-medium text-slate-800 outline-none transition hover:border-slate-300 focus:border-[#007BFF] focus:ring-1 focus:ring-[#007BFF]/20 disabled:cursor-not-allowed disabled:bg-slate-50";

const noteTextareaClass =
  "min-h-[4.5rem] w-full resize-none overflow-hidden rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-[12px] font-medium leading-[1.45] text-slate-800 outline-none transition hover:border-slate-300 focus:border-[#007BFF] focus:ring-1 focus:ring-[#007BFF]/20 disabled:cursor-not-allowed disabled:bg-slate-50";

function PlanNoteTextarea({ value, onChange, disabled, placeholder }) {
  const textareaRef = useRef(null);

  const resizeTextarea = useCallback(() => {
    const element = textareaRef.current;
    if (!element) return;
    element.style.height = "auto";
    element.style.height = `${element.scrollHeight}px`;
  }, []);

  useEffect(() => {
    resizeTextarea();
  }, [value, resizeTextarea]);

  return (
    <textarea
      ref={textareaRef}
      rows={2}
      value={value}
      disabled={disabled}
      placeholder={placeholder}
      onChange={(event) => {
        onChange(event.target.value);
        requestAnimationFrame(resizeTextarea);
      }}
      onFocus={resizeTextarea}
      className={noteTextareaClass}
    />
  );
}

function Chip({ children, tone = "slate" }) {
  const tones = {
    slate: "border-slate-200 bg-slate-50 text-slate-600",
    blue: "border-[#007BFF]/20 bg-[#007BFF]/8 text-[#007BFF]",
    amber: "border-amber-200 bg-amber-50 text-amber-700",
    green: "border-emerald-200 bg-emerald-50 text-emerald-700",
  };
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold capitalize ${tones[tone] || tones.slate}`}
    >
      {children}
    </span>
  );
}

function FieldLabel({ children, hint }) {
  return (
    <div className="mb-1 flex items-center justify-between gap-2">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
        {children}
      </span>
      {hint ? <span className="text-[10px] text-slate-400">{hint}</span> : null}
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="min-w-0">
      <p className="mb-0 text-[9px] font-medium uppercase tracking-wide text-slate-400">
        {label}
      </p>
      <p className="mb-0 mt-0.5 truncate text-[12px] font-semibold text-slate-800">
        {value || "—"}
      </p>
    </div>
  );
}

function toApiGstType(value) {
  if (value === "inclusive" || value === "exclusive") return value;
  return "none";
}

function fromApiGstType(value) {
  if (value === "inclusive" || value === "exclusive") return value;
  return "notax";
}

function taxLabelText(value) {
  const v = fromApiGstType(value);
  if (v === "inclusive") return "Inclusive";
  if (v === "exclusive") return "Exclusive";
  return "No Tax";
}

function taxFormulaText(value) {
  const v = fromApiGstType(value);
  if (v === "inclusive") return "GST = Amount × Rate / (100 + Rate)";
  if (v === "exclusive") return "GST = Amount × Rate / 100";
  return "No GST applied";
}

function toNumberSafe(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
}

const metricBoxClass =
  "min-w-0 flex-1 rounded-lg bg-[#F0F7FF] px-3 py-2.5 ring-1 ring-[#007BFF]/10";

function PlanMetricBox({
  label,
  value,
  sublabel,
  amountStack = null,
  stackBelow = null,
}) {
  const hasStack = Boolean(amountStack || stackBelow);
  return (
    <div className={`${metricBoxClass}${hasStack ? " min-w-[11rem]" : ""}`}>
      <p className="mb-0 text-[9px] font-semibold uppercase tracking-wide text-[#007BFF]/70">
        {label}
      </p>
      {amountStack ? (
        <>
          {sublabel ? (
            <p className="mb-2 mt-1 text-[10px] text-slate-500">{sublabel}</p>
          ) : null}
          <FeeAmountStack
            total={amountStack.total}
            paid={amountStack.paid}
            balance={amountStack.balance}
            size="lg"
          />
        </>
      ) : (
        <>
          <p className="mb-0 mt-0.5 truncate text-[18px] font-bold leading-tight tabular-nums text-[#007BFF]">
            {value}
          </p>
          {sublabel ? (
            <p className="mb-0 mt-1 text-[10px] text-slate-500">{sublabel}</p>
          ) : null}
          {stackBelow ? (
            <div className="mt-3 border-t-2 border-[#007BFF]/20 pt-2.5">
              <FeeAmountStack
                paid={stackBelow.paid}
                balance={stackBelow.balance}
                size="lg"
                hideTotal
              />
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

function PlanSummaryRow({
  tuitionFee,
  commissionableTuitionFee,
  remainingCommissionableFee,
  totalPaidTuition,
  installmentLabel,
  gstViews,
  currency,
  commissionRate,
  commissionGstType,
  commissionReceived,
  balanceCommission,
}) {
  const rateLabel = formatPercent(commissionRate, 2);

  const tuitionAmountStack = {
    total: formatCurrency(commissionableTuitionFee, currency),
    paid: formatCurrency(totalPaidTuition, currency),
    balance: formatCurrency(remainingCommissionableFee, currency),
  };

  const receivedPair = commissionPlanGstPair(
    commissionReceived,
    commissionGstType,
    DEFAULT_COMMISSION_GST_PERCENT,
  );
  const balancePair = commissionPlanGstPair(
    balanceCommission,
    commissionGstType,
    DEFAULT_COMMISSION_GST_PERCENT,
  );

  const commissionStackBelowExclusive = {
    paid: formatCurrency(receivedPair.exclusive, currency),
    balance: formatCurrency(balancePair.exclusive, currency),
  };

  const commissionStackBelowInclusive = {
    paid: formatCurrency(receivedPair.inclusive, currency),
    balance: formatCurrency(balancePair.inclusive, currency),
  };

  return (
    <div className="flex flex-row items-stretch gap-2 overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
      <PlanMetricBox
        label="Total Tuition Fee"
        value={formatCurrency(tuitionFee, currency)}
        sublabel={installmentLabel}
      />
      <PlanMetricBox
        label="Total Commissionable Fee"
        sublabel={installmentLabel}
        amountStack={tuitionAmountStack}
      />
      <PlanMetricBox
        label="Expected Commission (Exclusive)"
        value={formatCurrency(gstViews.baseExGst, currency)}
        sublabel={rateLabel}
        stackBelow={commissionStackBelowExclusive}
      />
      <PlanMetricBox
        label="Expected Commission (Inclusive)"
        value={formatCurrency(gstViews.exclusiveTotal, currency)}
        sublabel={rateLabel}
        stackBelow={commissionStackBelowInclusive}
      />
    </div>
  );
}

function buildCommissionTaxRows({
  gstType,
  currency,
  baseExGst,
  exclusiveTotal,
  inclusiveTotal,
  fallbackAmount = 0,
}) {
  const tax = fromApiGstType(gstType);
  if (tax === "notax") {
    return [
      {
        label: "Expected commission",
        value: formatCurrency(fallbackAmount || baseExGst, currency),
      },
    ];
  }

  return [
    {
      label: "Expected commission",
      value: formatCurrency(baseExGst, currency),
    },
    {
      label: "Expected commission with GST",
      value: formatCurrency(
        tax === "inclusive" ? inclusiveTotal : exclusiveTotal,
        currency,
      ),
    },
  ];
}

function PlanExpectedCommissionPreview({
  gstType,
  currency,
  baseExGst,
  exclusiveTotal,
  inclusiveTotal,
  fallbackAmount = 0,
}) {
  const tax = fromApiGstType(gstType);
  const rows = buildCommissionTaxRows({
    gstType,
    currency,
    baseExGst,
    exclusiveTotal,
    inclusiveTotal,
    fallbackAmount,
  });

  return (
    <div className="rounded-lg bg-[#F0F7FF] px-3 py-2.5 ring-1 ring-[#007BFF]/10">
      <div
        className={`grid gap-3 ${
          rows.length > 1 ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1"
        }`}
      >
        {rows.map((row) => (
          <div key={row.label}>
            <p className="mb-0 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
              {row.label}
            </p>
            <p className="mb-0 mt-0.5 text-[22px] font-bold leading-tight tabular-nums text-[#007BFF]">
              {row.value}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

function PlanSavedView({ plan, currency, bonus = null }) {
  const displayCurrency = plan.currency || currency || "AUD";
  const commissionableForCalc = isBonusFromCommissionableEnabled(plan)
    ? resolveEffectiveCommissionableTuitionFee({
        plan,
        bonus,
        tuitionFee: plan.tuitionFee,
        commissionableTuitionFee: plan.commissionableTuitionFee,
      })
    : plan.commissionableTuitionFee;
  const savedCommissionType = isFixedCommissionType(plan.commissionType)
    ? "fixed"
    : "percentage";
  const gstViews = calculateCommissionGstViews({
    tuitionFee: plan.tuitionFee,
    commissionableTuitionFee: commissionableForCalc,
    commissionType: savedCommissionType,
    commissionRate: plan.commissionRate,
    commissionGstType: plan.commissionGstType,
  });

  const useLiveCommissionBase = isBonusFromCommissionableEnabled(plan);
  const baseExGst = useLiveCommissionBase
    ? gstViews.baseExGst
    : plan.expectedCommissionExGst || gstViews.baseExGst;
  const exclusiveTotal = useLiveCommissionBase
    ? gstViews.exclusiveTotal
    : plan.expectedCommissionExclusive || gstViews.exclusiveTotal;
  const inclusiveTotal = useLiveCommissionBase
    ? gstViews.inclusiveTotal
    : plan.expectedCommissionInclusive || gstViews.inclusiveTotal;

  return (
    <SetupCardSavedBody
      heroLabel="Expected commission"
      primaryValue={
        savedCommissionType === "fixed"
          ? formatCurrency(plan.commissionRate, displayCurrency)
          : formatPercent(plan.commissionRate, 2)
      }
      primaryMeta={[
        savedCommissionType === "fixed" ? "Fixed amount" : null,
        taxLabelText(plan.commissionGstType),
        displayCurrency,
      ]
        .filter(Boolean)
        .join(" · ")}
      taxRows={buildCommissionTaxRows({
        gstType: plan.commissionGstType,
        currency: displayCurrency,
        baseExGst,
        exclusiveTotal,
        inclusiveTotal,
        fallbackAmount: plan.expectedCommission,
      })}
      taxRowsInline
      note={plan.note || plan.remarks || ""}
      tone="blue"
    />
  );
}

function CommissionDetailPlanCard({
  planContext,
  params = {},
  student,
  provider,
  course,
  application,
  currency: currencyProp = "AUD",
  onCreated,
  onDraftChange,
}) {
  const defaults = planContext?.defaults || {};
  const existing = planContext?.existing || null;
  const existingBonus = Array.isArray(planContext?.bonuses)
    ? planContext.bonuses[0] || null
    : null;
  const planLoading = Boolean(planContext?.loading);
  const installmentCount = Array.isArray(planContext?.installments)
    ? planContext.installments.length
    : 0;

  const location =
    existing?.location ||
    defaults.location ||
    application?.location ||
    "offshore";
  // Always prefer latest inquiry/course tuition over stale plan snapshot
  const tuitionFee = toNumberSafe(
    defaults.tuitionFee ?? course?.tuitionFee ?? existing?.tuitionFee,
  );
  const commissionableTuitionFee = toNumberSafe(
    defaults.commissionableTuitionFee ??
      course?.commissionableTuitionFee ??
      existing?.commissionableTuitionFee,
  );
  const suggestedRate = resolveRateForLocation({
    location,
    onshoreRate: defaults.onshoreRate,
    offshoreRate: defaults.offshoreRate,
  });

  const [isEditing, setIsEditing] = useState(false);
  const [commissionType, setCommissionType] = useState("percentage");
  const [commissionRate, setCommissionRate] = useState("");
  const [taxType, setTaxType] = useState("exclusive");
  const [currency, setCurrency] = useState(currencyProp || "AUD");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const resetForm = (plan) => {
    if (plan) {
      setCommissionType(
        isFixedCommissionType(plan.commissionType) ? "fixed" : "percentage",
      );
      setCommissionRate(String(plan.commissionRate ?? ""));
      setTaxType(fromApiGstType(plan.commissionGstType));
      setCurrency(plan.currency || currencyProp || "AUD");
      setNote(plan.note || plan.remarks || "");
      return;
    }
    setCommissionType("percentage");
    setCommissionRate(String(suggestedRate || defaults.commissionRate || ""));
    setTaxType(fromApiGstType(defaults.commissionGstType));
    setCurrency(defaults.currency || currencyProp || "AUD");
    setNote("");
  };

  const handleCommissionTypeChange = (next) => {
    if (next === commissionType) return;
    setCommissionType(next);
    if (next === "fixed") {
      setCommissionRate("");
      return;
    }
    const percentageRate =
      existing && !isFixedCommissionType(existing.commissionType)
        ? existing.commissionRate
        : suggestedRate || defaults.commissionRate || "";
    setCommissionRate(String(percentageRate ?? ""));
  };

  useEffect(() => {
    setIsEditing(false);
    resetForm(existing);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existing?.id, defaults.applicationCourseId, suggestedRate, currencyProp]);

  const commissionGstType = toApiGstType(taxType);
  const commissionableForCommissionCalc = useMemo(
    () =>
      resolveEffectiveCommissionableTuitionFee({
        plan: existing,
        bonus: existingBonus,
        tuitionFee,
        commissionableTuitionFee,
      }),
    [existing, existingBonus, tuitionFee, commissionableTuitionFee],
  );
  const preview = useMemo(
    () =>
      calculateExpectedCommission({
        tuitionFee,
        commissionableTuitionFee: commissionableForCommissionCalc,
        commissionType,
        commissionRate,
        commissionGstType,
      }),
    [
      tuitionFee,
      commissionableForCommissionCalc,
      commissionType,
      commissionRate,
      commissionGstType,
    ],
  );
  const previewGstViews = useMemo(
    () =>
      calculateCommissionGstViews({
        tuitionFee,
        commissionableTuitionFee: commissionableForCommissionCalc,
        commissionType,
        commissionRate,
        commissionGstType,
      }),
    [
      tuitionFee,
      commissionableForCommissionCalc,
      commissionType,
      commissionRate,
      commissionGstType,
    ],
  );

  useEffect(() => {
    if (!onDraftChange) return undefined;
    const editingOrCreating = !existing || isEditing;
    if (!editingOrCreating) {
      onDraftChange(null);
      return undefined;
    }
    onDraftChange({
      commissionType,
      commissionRate: Number(commissionRate) || 0,
      commissionGstType,
    });
    return () => onDraftChange(null);
  }, [
    onDraftChange,
    existing,
    isEditing,
    commissionType,
    commissionRate,
    commissionGstType,
  ]);

  const canSubmit =
    !saving &&
    defaults.applicationCourseId &&
    (params.appId || application?.id) &&
    Number(commissionRate) >= 0;

  const handleSave = async () => {
    if (!canSubmit) return;
    if (!defaults.applicationCourseId) {
      Swal.fire(
        "Missing course",
        "Application course id is required to save a commission plan.",
        "error",
      );
      return;
    }

    setSaving(true);
    try {
      const payload = buildNewCommissionPlanPayload({
        applicationId: params.appId || application?.id,
        applicationCourseId: defaults.applicationCourseId,
        clientId: params.studentId || student?.id,
        providerId: params.providerId || provider?.id,
        courseId: params.courseId || course?.id,
        levelId: defaults.levelId || course?.levelId,
        location,
        tuitionFee,
        commissionable_tuition_fee: commissionableTuitionFee,
        commissionType,
        commissionRate,
        commissionGstType,
        expectedCommission: preview.expectedCommission,
        expectedCommissionExGst: previewGstViews.baseExGst,
        expectedCommissionExclusive: previewGstViews.exclusiveTotal,
        expectedCommissionInclusive: previewGstViews.inclusiveTotal,
        currency,
        status: existing?.status || "active",
        hasBonus: Boolean(existing?.hasBonus ?? existing?.has_bonus),
        bonusFromCommissionable: Boolean(
          existing?.bonusFromCommissionable ?? existing?.bonus_from_commissionable,
        ),
        remarks: note,
        note,
      });
      let planId = existing?.id || null;
      if (existing?.id && isEditing) {
        await updateNewCommissionPlan(existing.id, payload);
        planId = existing.id;
      } else {
        const createResponse = await createNewCommissionPlan(payload);
        planId = extractCreatedPlanId(createResponse);
      }

      setIsEditing(false);
      Swal.fire({
        icon: "success",
        title: existing?.id && isEditing ? "Plan updated" : "Plan created",
        text: "Commission plan saved successfully.",
        timer: 1400,
        showConfirmButton: false,
      });
      if (typeof onCreated === "function") await onCreated(planId);
    } catch (error) {
      Swal.fire(
        "Error",
        error?.message || "Failed to save commission plan.",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  const showForm = !existing || isEditing;

  return (
    <section
      className={`flex h-full flex-col overflow-hidden rounded-xl border bg-white shadow-sm transition ${
        isEditing ? "border-[#007BFF]/40" : "border-slate-200"
      }`}
    >
      <div className="flex items-center gap-2 border-b border-slate-100 bg-gradient-to-r from-[#F7FAFF] to-white px-3 py-2">
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-[#007BFF] text-white">
          <FaClipboardList size={11} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <h3 className="mb-0 text-[13px] font-semibold text-slate-900">
              Commission plan
            </h3>
            {existing ? (
              <Chip tone={isEditing ? "amber" : "green"}>
                {isEditing ? "Editing" : existing.status || "active"}
              </Chip>
            ) : (
              <Chip tone="amber">Setup</Chip>
            )}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-1.5">
          {existing && !isEditing ? (
            <button
              type="button"
              onClick={() => {
                resetForm(existing);
                setIsEditing(true);
              }}
              className="inline-flex h-7 items-center gap-1 rounded-md bg-[#007BFF] px-2 text-[10px] font-semibold text-white hover:bg-[#0069D9]"
              style={{ border: "none" }}
            >
              <FaEdit size={9} />
              Edit
            </button>
          ) : null}
          {isEditing ? (
            <>
              <button
                type="button"
                onClick={() => {
                  resetForm(existing);
                  setIsEditing(false);
                }}
                disabled={saving}
                className="inline-flex h-8 items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 text-[11px] font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-60"
              >
                <FaTimes size={9} />
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={!canSubmit}
                className={`inline-flex h-8 items-center gap-1 rounded-lg px-2.5 text-[11px] font-semibold text-white ${
                  canSubmit
                    ? "bg-[#007BFF] hover:bg-[#0069D9]"
                    : "cursor-not-allowed bg-[#007BFF]/40"
                }`}
                style={{ border: "none" }}
              >
                <FaCheck size={9} />
                {saving ? "Saving…" : "Save"}
              </button>
            </>
          ) : null}
        </div>
      </div>

      <div className="flex flex-col gap-2.5 p-2.5">
        {planLoading && !existing ? (
          <div className="flex items-center justify-center py-6 text-[12px] text-slate-400">
            Loading saved plan…
          </div>
        ) : !showForm ? (
          <PlanSavedView
            plan={existing}
            currency={existing.currency || currency}
            bonus={existingBonus}
          />
        ) : (
          <div className="flex flex-col gap-2.5">
            <div className="flex flex-wrap gap-1.5">
              <Chip tone="blue">
                <span className="inline-flex items-center gap-1">
                  <FaMapMarkerAlt size={8} />
                  {location}
                </span>
              </Chip>
              <Chip>
                Expected tuition{" "}
                {formatCurrency(commissionableTuitionFee, currency)}
              </Chip>
              {!isFixedCommissionType(commissionType) ? (
                <Chip>Suggested {formatPercent(suggestedRate, 2)}</Chip>
              ) : (
                <Chip>Plain amount</Chip>
              )}
            </div>

            <div className="grid grid-cols-2 gap-2.5">
              <div className="col-span-2">
                <FieldLabel>Commission type</FieldLabel>
                <div className="inline-flex overflow-hidden rounded-lg border border-slate-200 bg-white p-0.5">
                  {COMMISSION_TYPE_OPTIONS.map((option) => {
                    const active = commissionType === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        disabled={saving}
                        onClick={() => handleCommissionTypeChange(option.value)}
                        className={`h-8 min-w-[108px] rounded-md px-3 text-[11px] font-semibold transition ${
                          active
                            ? "bg-[#007BFF] text-white shadow-sm"
                            : "text-slate-600 hover:bg-slate-50"
                        } ${saving ? "cursor-not-allowed opacity-60" : ""}`}
                        style={{ border: "none" }}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <label className="block min-w-0">
                <FieldLabel
                  hint={
                    isFixedCommissionType(commissionType)
                      ? currency || "Amount"
                      : "% of tuition"
                  }
                >
                  {isFixedCommissionType(commissionType)
                    ? "Commission amount"
                    : "Commission %"}
                </FieldLabel>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={commissionRate}
                  disabled={saving}
                  onChange={(e) => setCommissionRate(e.target.value)}
                  className={controlClass}
                  placeholder="0"
                />
              </label>
              <label className="block min-w-0">
                <FieldLabel>Currency</FieldLabel>
                <select
                  value={currency}
                  disabled={saving}
                  onChange={(e) => setCurrency(e.target.value)}
                  className={controlClass}
                >
                  <option value="">Select</option>
                  {CURRENCY_OPTIONS.map((code) => (
                    <option key={code} value={code}>
                      {code}
                    </option>
                  ))}
                </select>
              </label>
              <label className="col-span-2 block min-w-0">
                <FieldLabel>Tax</FieldLabel>
                <select
                  value={taxType}
                  disabled={saving}
                  onChange={(e) => setTaxType(e.target.value)}
                  className={controlClass}
                >
                  <option value="inclusive">Inclusive</option>
                  <option value="exclusive">Exclusive</option>
                  <option value="notax">No Tax</option>
                </select>
                <p className="mb-0 mt-1.5 text-[11px] text-slate-400">
                  {taxFormulaText(taxType)}
                </p>
              </label>
              <label className="col-span-2 block min-w-0">
                <FieldLabel hint="Optional">Note</FieldLabel>
                <PlanNoteTextarea
                  value={note}
                  disabled={saving}
                  onChange={setNote}
                  placeholder="Optional note…"
                />
              </label>
            </div>

            <PlanExpectedCommissionPreview
              gstType={commissionGstType}
              currency={currency}
              baseExGst={previewGstViews.baseExGst}
              exclusiveTotal={previewGstViews.exclusiveTotal}
              inclusiveTotal={previewGstViews.inclusiveTotal}
              fallbackAmount={preview.expectedCommission}
            />

            {!isEditing ? (
              <button
                type="button"
                onClick={handleSave}
                disabled={!canSubmit}
                className={`inline-flex h-10 w-full items-center justify-center gap-1.5 rounded-lg text-[13px] font-semibold text-white transition ${
                  canSubmit
                    ? "bg-[#007BFF] hover:bg-[#0069D9]"
                    : "cursor-not-allowed bg-[#007BFF]/40"
                }`}
                style={{ border: "none" }}
              >
                <FaCheck size={11} />
                {saving ? "Creating…" : "Create commission plan"}
              </button>
            ) : null}
          </div>
        )}
      </div>
    </section>
  );
}

export default CommissionDetailPlanCard;
